// OpenRouter API Service
const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';
const OPENROUTER_MODELS_API_URL = 'https://openrouter.ai/api/v1/models';

// Cache configuration
const CACHE_KEY = 'openrouter_models_cache';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// Available AI Models - Free models at the top (Verified from OpenRouter)
// Extended with isAvailable, lastChecked, and source fields
export const AI_MODELS = [
  // FREE MODELS (All verified working models from OpenRouter)
  { id: 'openai/gpt-3.5-turbo', name: 'OpenAI GPT-3.5 Turbo', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'deepseek/deepseek-chat-v3.1:free', name: 'DeepSeek V3.1', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'nvidia/nemotron-nano-9b-v2:free', name: 'NVIDIA Nemotron Nano 9B V2', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'openai/gpt-oss-20b:free', name: 'OpenAI GPT-OSS-20B', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'google/gemma-3n-e2b-it:free', name: 'Google Gemma 3n 2B', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'qwen/qwen3-coder:free', name: 'Qwen3 Coder 480B A35B', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'z-ai/glm-4.5-air:free', name: 'Z.AI GLM 4.5 Air', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'cognitivecomputations/dolphin-mistral-24b-venice-edition:free', name: 'Venice Uncensored', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'tngtech/deepseek-r1t2-chimera:free', name: 'TNG DeepSeek R1T2 Chimera', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'meituan/longcat-flash-chat:free', name: 'Chat (Longcat Flash)', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'meta-llama/llama-3.2-3b-instruct:free', name: 'Llama 3.2 3B Instruct', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'meta-llama/llama-3.1-8b-instruct:free', name: 'Llama 3.1 8B Instruct', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'qwen/qwen-2-7b-instruct:free', name: 'Qwen 2 7B Instruct', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'mistralai/mistral-7b-instruct:free', name: 'Mistral 7B Instruct', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'google/gemini-2.0-flash-exp:free', name: 'Google Gemini 2.0 Flash Experimental', isFree: true, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  
  // PAID MODELS
  { id: 'openai/gpt-4o', name: 'GPT-4o', isFree: false, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'openai/gpt-4-turbo', name: 'GPT-4 Turbo', isFree: false, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'anthropic/claude-3.5-sonnet', name: 'Claude 3.5 Sonnet', isFree: false, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'anthropic/claude-3-opus', name: 'Claude 3 Opus', isFree: false, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'google/gemini-pro-1.5', name: 'Gemini Pro 1.5', isFree: false, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'meta-llama/llama-3.1-70b-instruct', name: 'Llama 3.1 70B', isFree: false, isAvailable: true, lastChecked: null, source: 'hardcoded' },
  { id: 'anthropic/claude-3-sonnet', name: 'Claude 3 Sonnet', isFree: false, isAvailable: true, lastChecked: null, source: 'hardcoded' }
];

/**
 * Normalize API message content (string or array of parts) to a single string.
 * Handles multimodal responses (text + image_url) so Work Plan and other callers get usable content.
 * @param {string|Array} content - choice.message.content from OpenRouter (string or array of { type, text?, image_url? })
 * @returns {string}
 */
function normalizeMessageContent(content) {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return String(content);
  let out = '';
  for (const part of content) {
    if (part.type === 'text' && part.text) out += part.text;
    if ((part.type === 'image_url' || part.type === 'image') && (part.image_url || part.url)) {
      const url = part.image_url?.url || part.image_url || part.url || part.content;
      if (url) out += `\n\n![Generated Image](${url})\n\n`;
    }
  }
  return out.trim();
}

/**
 * Generate content using OpenRouter API (supports both single prompt and chat messages array)
 * @param {string|Array} promptOrMessages - The prompt string or array of message objects
 * @param {string} model - The model ID to use
 * @param {string} apiKey - OpenRouter API key
 * @returns {Promise<string>} The generated content
 */
export async function generateContent(promptOrMessages, model, apiKey, onChunk) {
  if (!apiKey) {
    throw new Error('API key is required');
  }

  // Handle both single prompt string and messages array
  let messages;
  if (typeof promptOrMessages === 'string') {
    if (!promptOrMessages) {
      throw new Error('Prompt is required');
    }
    messages = [
      {
        role: 'user',
        content: promptOrMessages
      }
    ];
  } else if (Array.isArray(promptOrMessages)) {
    if (promptOrMessages.length === 0) {
      throw new Error('Messages array cannot be empty');
    }
    messages = promptOrMessages;
  } else {
    throw new Error('Invalid prompt or messages format');
  }

  try {
    const doRequest = async (modelId) => fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': 'https://fiddyscript.com',
        'X-Title': 'Fiddyscript AI Content Assistant'
      },
      body: JSON.stringify({
        model: modelId,
        messages: messages,
        stream: onChunk ? true : false, // Enable streaming if callback provided
        max_tokens: 2048, // Limit response length for faster responses
      })
    });

    let response = await doRequest(model);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.error?.message || errorData.error || `API request failed with status ${response.status}`;
      
      // Log full error for debugging
      console.error('OpenRouter API Error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        model: model
      });

      // If DeepSeek v3.1 free endpoint is unavailable on this key, retry with generic DeepSeek free endpoint
      const isNoEndpoint = /No endpoints found/i.test(message) || response.status === 404 || response.status === 400;
      const isDeepseekV31 = /deepseek\/deepseek-chat-v3\.1:free/i.test(model);
      if (isNoEndpoint && isDeepseekV31) {
        const fallbackModel = 'deepseek/deepseek-chat:free';
        try {
          const retryRes = await doRequest(fallbackModel);
          if (retryRes.ok) {
            const retryData = await retryRes.json();
            return retryData.choices?.[0]?.message?.content || '';
          }
        } catch (_) {}
      }

      // Friendly messages for common cases
      if (response.status === 402) {
        throw new Error('OpenRouter returned 402 (payment required). Even for free models, the API can require a positive balance—the dashboard may show $0 but the system may still block requests. Add a small amount at https://openrouter.ai/credits (e.g. $1) to unlock free model access. Your API key is valid.');
      }
      if (response.status === 429) {
        throw new Error('Rate limit (429). Too many requests. Please wait 30–60 seconds and try again, or reduce request frequency.');
      }
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authorization error (401/403). Please verify your OpenRouter API key in Settings.');
      }
      if (/No endpoints found matching your data policy/i.test(message)) {
        throw new Error('Free model privacy setting mismatch. Enable "Enable free endpoints that may publish prompts" at https://openrouter.ai/settings/privacy to use some FREE models.');
      }
      if (/Provider returned error/i.test(message) || errorData.error?.type === 'provider_error') {
        // Provider error usually means the model doesn't support the request format
        // Try to extract more details or provide helpful message
        const providerMessage = errorData.error?.message || message;
        throw new Error(`Model provider error: ${providerMessage}. Try using a different AI model in Settings.`);
      }
      throw new Error(message);
    }

    // Handle streaming response
    // Check content-type to see if it's streaming, even if onChunk not provided
    const contentType = response.headers.get('content-type') || '';
    const isStreamingResponse = contentType.includes('text/event-stream') || contentType.includes('text/plain');
    
    // If it's a streaming response, we MUST handle it as streaming, even without onChunk
    if (response.body && isStreamingResponse) {
      console.log('🌊 Starting to read streaming response...');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';
      let lineCount = 0;
      let dataLineCount = 0;
      // Track extracted image URLs to prevent duplicates
      const extractedImageUrls = new Set();
      // Only emit the first image from stream (Gemini can send multiple chunks with different images)
      let streamHasEmittedImage = false;
      
      // If onChunk not provided but we have streaming response, create a no-op callback
      const chunkCallback = onChunk || (() => {});

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log('✅ Stream reading completed (done=true)');
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          lineCount += lines.length;

          for (const line of lines) {
            // Skip SSE comment lines (start with ':')
            if (line.startsWith(':')) {
              continue;
            }
            
            // Only process lines that start with 'data: ' (with space after colon)
            if (!line.startsWith('data: ')) {
              // Skip non-data lines (empty lines, comments, etc.)
              // Also skip lines that start with 'data:' without space (malformed)
              continue;
            }
            
            dataLineCount++;
            const data = line.slice(6); // Remove 'data: ' prefix
            if (data === '[DONE]') {
              console.log('🏁 Received [DONE] marker');
              continue;
            }

            // Skip empty or whitespace-only lines immediately
            if (!data || !data.trim()) {
              continue;
            }

            // CRITICAL: Validate JSON structure BEFORE attempting to parse
            // This prevents "Unexpected character" errors
            const trimmed = data.trim();
            
            // Must be at least 2 characters to be valid JSON
            if (trimmed.length < 2) {
              continue;
            }
            
            const firstChar = trimmed[0];
            const lastChar = trimmed[trimmed.length - 1];
            
            // JSON must start with { or [ - reject anything else (including :, ", ', etc.)
            if (firstChar !== '{' && firstChar !== '[') {
              // Silently skip - this prevents "Unexpected character" errors
              continue;
            }
            
            // JSON must end with } or ] - reject incomplete JSON
            if (lastChar !== '}' && lastChar !== ']') {
              // Silently skip incomplete JSON
              continue;
            }
            
            // Validate balanced braces/brackets (accounting for strings)
            let openBraces = 0;
            let openBrackets = 0;
            let inString = false;
            let escapeNext = false;
            
            for (let i = 0; i < trimmed.length; i++) {
              const char = trimmed[i];
              
              if (escapeNext) {
                escapeNext = false;
                continue;
              }
              
              if (char === '\\') {
                escapeNext = true;
                continue;
              }
              
              if (char === '"' && !escapeNext) {
                inString = !inString;
                continue;
              }
              
              if (!inString) {
                if (char === '{') openBraces++;
                else if (char === '}') openBraces--;
                else if (char === '[') openBrackets++;
                else if (char === ']') openBrackets--;
              }
            }
            
            // Skip if braces/brackets are unbalanced
            if (openBraces !== 0 || openBrackets !== 0) {
              continue;
            }

            try {
              // Only attempt to parse if all validations passed
              const parsed = JSON.parse(data);
              console.log(`📥 Parsed data line #${dataLineCount}:`, JSON.stringify(parsed, null, 2).substring(0, 500));
              
              // Try multiple content extraction methods for different response formats
              let delta = '';
              
              // Check for images in delta.images (Gemini image generation format)
              // Only use the first image chunk; Gemini may send multiple chunks with different images (e.g. comparison)
              if (parsed.choices?.[0]?.delta?.images && Array.isArray(parsed.choices[0].delta.images) && !streamHasEmittedImage) {
                console.log('🖼️ Found images array in delta.images:', parsed.choices[0].delta.images.length, 'images');
                const imageParts = parsed.choices[0].delta.images
                  .map((img, idx) => {
                    if (streamHasEmittedImage) return '';
                    const imageUrl = img.image_url?.url || img.image_url || img.url || img.data;
                    if (imageUrl) {
                      // For base64 images, use first 200 chars as identifier to avoid memory issues
                      // Full base64 strings can be very long and might have slight variations
                      const imageId = imageUrl.length > 200 
                        ? imageUrl.substring(0, 200) + '...' + imageUrl.substring(imageUrl.length - 50)
                        : imageUrl;
                      
                      // Check if this image has already been extracted
                      if (extractedImageUrls.has(imageId)) {
                        console.log(`🔄 Skipping duplicate image #${idx + 1} (already extracted)`);
                        return '';
                      }
                      // Mark this image as extracted and only allow one image per stream
                      extractedImageUrls.add(imageId);
                      streamHasEmittedImage = true;
                      console.log(`🖼️ Extracted image #${idx + 1} URL (${imageUrl.length} chars):`, imageUrl.substring(0, 100));
                      return `\n\n![Generated Image](${imageUrl})\n\n`;
                    }
                    return '';
                  })
                  .filter(Boolean)
                  .join('');
                if (imageParts) {
                  delta = imageParts;
                }
              } else if (parsed.choices?.[0]?.delta?.images && Array.isArray(parsed.choices[0].delta.images) && streamHasEmittedImage) {
                console.log('🖼️ Skipping additional image chunk (only one image per response)');
              }
              
              // Standard streaming format (text content)
              if (parsed.choices?.[0]?.delta?.content) {
                const content = parsed.choices[0].delta.content;
                // Ensure content is always a string to prevent "[object Object]"
                const contentStr = typeof content === 'string' ? content : String(content || '');
                delta += contentStr;
              }
              // Gemini-style delta.parts (array of { text } or { inlineData: { data, mimeType } })
              else if (Array.isArray(parsed.choices?.[0]?.delta?.parts)) {
                const parts = parsed.choices[0].delta.parts;
                for (const part of parts) {
                  if (part.text) delta += part.text;
                  if (part.inlineData?.data) {
                    const mime = part.inlineData.mimeType || 'image/png';
                    const dataUrl = `data:${mime};base64,${part.inlineData.data}`;
                    const imageId = dataUrl.slice(0, 150) + dataUrl.slice(-50);
                    if (!extractedImageUrls.has(imageId)) {
                      extractedImageUrls.add(imageId);
                      delta += `\n\n![Generated Image](${dataUrl})\n\n`;
                    }
                  }
                }
              }
              // Some models return content in message field during streaming
              else if (parsed.choices?.[0]?.message?.content) {
                const messageContent = parsed.choices[0].message.content;
                // Only use if we haven't already added this content
                if (typeof messageContent === 'string' && !fullContent.includes(messageContent)) {
                  delta += messageContent;
                }
              }
              // Handle array content (multimodal responses)
              else if (Array.isArray(parsed.choices?.[0]?.delta?.content)) {
                const contentArray = parsed.choices[0].delta.content;
                // Debug logging
                if (process.env.NODE_ENV === 'development') {
                  console.log('Multimodal delta content array:', JSON.stringify(contentArray, null, 2));
                }
                // Extract text content
                const textParts = contentArray
                  .filter(item => item.type === 'text')
                  .map(item => item.text)
                  .join('');
                // Extract image URLs and append as markdown images
                const imageParts = contentArray
                  .filter(item => item.type === 'image_url' || item.type === 'image' || item.type === 'image_url' || item.image_url)
                  .map(item => {
                    const imageUrl = item.image_url?.url || item.image_url || item.url || item.image || item.content;
                    if (imageUrl) {
                      // For base64 images, use first 200 chars as identifier to avoid memory issues
                      const imageId = imageUrl.length > 200 
                        ? imageUrl.substring(0, 200) + '...' + imageUrl.substring(imageUrl.length - 50)
                        : imageUrl;
                      
                      // Check if this image has already been extracted
                      if (extractedImageUrls.has(imageId)) {
                        if (process.env.NODE_ENV === 'development') {
                          console.log('🔄 Skipping duplicate image URL in delta array (already extracted)');
                        }
                        return '';
                      }
                      // Mark this image as extracted
                      extractedImageUrls.add(imageId);
                      if (process.env.NODE_ENV === 'development') {
                        console.log('Found image URL in delta:', imageUrl.substring(0, 100));
                      }
                      return `\n\n![Generated Image](${imageUrl})\n\n`;
                    }
                    return '';
                  })
                  .join('');
                delta = textParts + imageParts;
              }
              // Handle array content in message
              else if (Array.isArray(parsed.choices?.[0]?.message?.content)) {
                const contentArray = parsed.choices[0].message.content;
                // Debug logging
                if (process.env.NODE_ENV === 'development') {
                  console.log('Multimodal message content array:', JSON.stringify(contentArray, null, 2));
                }
                // Extract text content
                const textParts = contentArray
                  .filter(item => item.type === 'text')
                  .map(item => item.text)
                  .join('');
                // Extract image URLs and append as markdown images
                const imageParts = contentArray
                  .filter(item => item.type === 'image_url' || item.type === 'image' || item.image_url)
                  .map(item => {
                    const imageUrl = item.image_url?.url || item.image_url || item.url || item.image || item.content;
                    if (imageUrl) {
                      // For base64 images, use first 200 chars as identifier to avoid memory issues
                      const imageId = imageUrl.length > 200 
                        ? imageUrl.substring(0, 200) + '...' + imageUrl.substring(imageUrl.length - 50)
                        : imageUrl;
                      
                      // Check if this image has already been extracted
                      if (extractedImageUrls.has(imageId)) {
                        if (process.env.NODE_ENV === 'development') {
                          console.log('🔄 Skipping duplicate image URL in message array (already extracted)');
                        }
                        return '';
                      }
                      // Mark this image as extracted
                      extractedImageUrls.add(imageId);
                      if (process.env.NODE_ENV === 'development') {
                        console.log('Found image URL in message:', imageUrl.substring(0, 100));
                      }
                      return `\n\n![Generated Image](${imageUrl})\n\n`;
                    }
                    return '';
                  })
                  .join('');
                const messageContent = textParts + imageParts;
                if (messageContent && !fullContent.includes(messageContent)) {
                  delta = messageContent;
                }
              }
              
              // Also check for images in other response structures (if not already found)
              // NOTE: Skip delta.images here since it's already handled above to prevent duplicates
              if (!delta || !delta.includes('![Generated Image]')) {
                const choice = parsed.choices?.[0];
                // Check for images in various locations (but NOT delta.images - already handled)
                if (choice?.delta?.image_url) {
                  const imageUrl = choice.delta.image_url.url || choice.delta.image_url;
                  // Use same ID logic for consistency
                  const imageId = imageUrl.length > 200 
                    ? imageUrl.substring(0, 200) + '...' + imageUrl.substring(imageUrl.length - 50)
                    : imageUrl;
                  if (imageUrl && !extractedImageUrls.has(imageId)) {
                    extractedImageUrls.add(imageId);
                    console.log('🖼️ Found image URL in delta.image_url:', imageUrl.substring(0, 100));
                    delta = (delta || '') + `\n\n![Generated Image](${imageUrl})\n\n`;
                  }
                } else if (choice?.message?.image_url) {
                  const imageUrl = choice.message.image_url.url || choice.message.image_url;
                  // Use same ID logic for consistency
                  const imageId = imageUrl.length > 200 
                    ? imageUrl.substring(0, 200) + '...' + imageUrl.substring(imageUrl.length - 50)
                    : imageUrl;
                  if (imageUrl && !extractedImageUrls.has(imageId)) {
                    extractedImageUrls.add(imageId);
                    console.log('🖼️ Found image URL in message.image_url:', imageUrl.substring(0, 100));
                    delta = (delta || '') + `\n\n![Generated Image](${imageUrl})\n\n`;
                  }
                }
                // Removed duplicate delta.images check - already handled above
              }
              
              // Ensure delta is always a string before using it
              const deltaStr = typeof delta === 'string' ? delta : String(delta || '');
              
              if (deltaStr) {
                console.log(`✨ Extracted delta content (${deltaStr.length} chars):`, deltaStr.substring(0, 200));
                fullContent += deltaStr;
                chunkCallback(deltaStr); // Call callback with each chunk - always a string
              } else {
                const choice = parsed.choices?.[0];
                const keys = choice ? Object.keys(choice) : [];
                const deltaKeys = choice?.delta ? Object.keys(choice.delta) : [];
                console.log('⚠️ No delta content extracted from this chunk. choice keys:', keys, 'delta keys:', deltaKeys, 'sample:', JSON.stringify(parsed).slice(0, 300));
              }
              } catch (e) {
                // Silently skip JSON parse errors - we've already validated the structure
                // If we get here, it means the validation passed but JSON.parse still failed
                // This is rare but can happen with edge cases - just skip the line
                // DO NOT log or throw - just continue silently to avoid error messages
                continue;
              }
          }
        }
        
        console.log(`📊 Stream processing complete: ${lineCount} total lines, ${dataLineCount} data lines`);
        // Ensure fullContent is always a string before returning
        const finalContent = typeof fullContent === 'string' ? fullContent : String(fullContent || '');
        console.log(`📏 Full content length: ${finalContent.length} characters`);
        
        // If no content was extracted but response completed, log for debugging
        if (!finalContent && response.ok) {
          console.warn('⚠️ No content extracted from streaming response but response was OK. Model may use an unsupported delta format. Check earlier logs for "No delta content extracted" to see chunk structure.');
          return '';
        }
        
        if (!finalContent) {
          console.error('❌ No content extracted from streaming response!');
        }
        
        return finalContent;
      } catch (streamError) {
        // If we have some content, return it even if there was an error
        // This allows partial responses to be displayed
        if (fullContent) {
          console.warn('⚠️ Stream reading error, but returning partial content:', streamError.message || String(streamError));
          // Ensure content is always a string
          return typeof fullContent === 'string' ? fullContent : String(fullContent || '');
        }
        // Only throw if we have no content at all
        console.error('❌ Error reading stream:', streamError);
        console.error('❌ Stream error details:', JSON.stringify(streamError, Object.getOwnPropertyNames(streamError)));
        throw streamError;
      } finally {
        console.log('🔓 Releasing stream reader lock');
        reader.releaseLock();
      }
    }

    // Non-streaming response (fallback)
    // Check the response text first to see if it's actually SSE format
    // (content-type header might be missing or incorrect)
    let responseText;
    try {
      // Clone before reading to preserve original
      const clonedResponse = response.clone();
      responseText = await clonedResponse.text();
      
      // Check if it's SSE format (even if content-type didn't indicate it)
      if (responseText.trim().startsWith(':') || 
          responseText.includes('OPENROUTER PROCESSING') || 
          responseText.includes('data:') ||
          (responseText.includes('\n') && responseText.split('\n').some(line => line.trim().startsWith('data: ')))) {
        // This is SSE format - process it as streaming
        const lines = responseText.split('\n');
        let fullContent = '';
        let sseHasEmittedImage = false;
        
        for (const line of lines) {
          // Skip SSE comment lines (start with ':')
          if (line.startsWith(':')) {
            continue;
          }
          
          // Only process lines that start with 'data: '
          if (!line.startsWith('data: ')) {
            continue;
          }
          
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          
          // Skip empty data lines
          if (!data || !data.trim()) {
            continue;
          }
          
          // Validate JSON structure before parsing
          const trimmed = data.trim();
          if (trimmed.length < 2) continue;
          
          const firstChar = trimmed[0];
          const lastChar = trimmed[trimmed.length - 1];
          
          if (firstChar !== '{' && firstChar !== '[') continue;
          if (lastChar !== '}' && lastChar !== ']') continue;
          
          try {
            const parsed = JSON.parse(data);
            // Extract content from parsed JSON
            if (parsed.choices?.[0]?.delta?.content) {
              const content = parsed.choices[0].delta.content;
              // Ensure content is always a string to prevent "[object Object]"
              const contentStr = typeof content === 'string' ? content : String(content || '');
              fullContent += contentStr;
            } else if (parsed.choices?.[0]?.delta?.images && !sseHasEmittedImage) {
              // Handle images - only first image per response
              const imgs = parsed.choices[0].delta.images;
              const firstImg = imgs.find((img) => {
                const imageUrl = img.image_url?.url || img.image_url || img.url || img.data;
                return !!imageUrl;
              });
              if (firstImg) {
                const imageUrl = firstImg.image_url?.url || firstImg.image_url || firstImg.url || firstImg.data;
                fullContent += `\n\n![Generated Image](${imageUrl})\n\n`;
                sseHasEmittedImage = true;
              }
            }
          } catch (e) {
            // Skip invalid JSON lines
            continue;
          }
        }
        
        // Ensure fullContent is always a string before returning
        return typeof fullContent === 'string' ? fullContent : String(fullContent || '');
      }
      
      // Not SSE format, try to parse as JSON
      try {
        const data = JSON.parse(responseText);
        if (data.choices && data.choices[0]) {
          const choice = data.choices[0];
          const content = choice.message?.content ?? choice.content;
          if (content != null) {
            return normalizeMessageContent(content);
          }
        }
      } catch (parseError) {
        // Not valid JSON either
        throw new Error(`Invalid JSON response from API: ${parseError.message || String(parseError)}`);
      }
    } catch (textError) {
      // If we can't read as text, try JSON directly
      try {
        const data = await response.json();
        if (data.choices && data.choices[0]) {
          const choice = data.choices[0];
          const content = choice.message?.content ?? choice.content;
          if (content != null) {
            return normalizeMessageContent(content);
          }
        }
      } catch (jsonError) {
        throw new Error(`Invalid JSON response from API: ${jsonError.message || String(jsonError)}`);
      }
    }
    
    throw new Error('Invalid response format from API');
  } catch (error) {
    console.error('OpenRouter API Error:', error);
    throw error;
  }
}

/**
 * Fetch models from OpenRouter API
 * @param {string} apiKey - OpenRouter API key (optional, but recommended for complete data)
 * @returns {Promise<Array>} List of models from API
 */
/**
 * Generate content using OpenRouter API with vision (multimodal) support
 * @param {string} imageBase64 - Base64 encoded image (without data URL prefix)
 * @param {string} prompt - The text prompt to send with the image
 * @param {string} model - The vision-capable model ID (e.g., 'openai/gpt-4o', 'anthropic/claude-3.5-sonnet')
 * @param {string} apiKey - OpenRouter API key
 * @returns {Promise<string>} The generated content
 */
export async function generateVisionContent(imageBase64, prompt, model, apiKey) {
  if (!apiKey) {
    throw new Error('API key is required');
  }

  if (!imageBase64) {
    throw new Error('Image base64 data is required');
  }

  if (!prompt) {
    throw new Error('Prompt is required');
  }

  // Remove data URL prefix if present
  const base64Data = imageBase64.replace(/^data:image\/[^;]+;base64,/, '');

  // Create multimodal message with image
  const messages = [
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: prompt
        },
        {
          type: 'image_url',
          image_url: {
            url: `data:image/png;base64,${base64Data}`
          }
        }
      ]
    }
  ];

  try {
    const response = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': 'https://fiddyscript.com',
        'X-Title': 'Fiddyscript AI Content Assistant'
      },
      body: JSON.stringify({
        model: model,
        messages: messages,
        max_tokens: 2048,
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.error?.message || errorData.error || `API request failed with status ${response.status}`;
      
      console.error('OpenRouter Vision API Error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        model: model
      });

      if (response.status === 402) {
        throw new Error('OpenRouter returned 402 (payment required). Even for free models, the API can require a positive balance—the dashboard may show $0 but the system may still block requests. Add a small amount at https://openrouter.ai/credits (e.g. $1) to unlock free model access. Your API key is valid.');
      }
      if (response.status === 429) {
        throw new Error('Rate limit (429). Too many requests. Please wait 30–60 seconds and try again.');
      }
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authorization error (401/403). Please verify your OpenRouter API key in Settings.');
      }
      throw new Error(message);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || '';
  } catch (error) {
    console.error('Vision API generation error:', error);
    throw new Error(`Vision API failed: ${error.message}`);
  }
}

export async function fetchModelsFromAPI(apiKey = null) {
  try {
    const headers = {
      'HTTP-Referer': 'https://fiddyscript.com',
      'X-Title': 'Fiddyscript AI Content Assistant'
    };
    
    if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }

    const response = await fetch(OPENROUTER_MODELS_API_URL, {
      method: 'GET',
      headers: headers
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.data || !Array.isArray(data.data)) {
      throw new Error('Invalid API response format');
    }

    // Transform API response to match our model structure
    const models = data.data.map(model => {
      const pricing = model.pricing || {};
      const isFree = pricing.prompt === '0' && pricing.completion === '0' || 
                     model.id.endsWith(':free') ||
                     (typeof pricing.prompt === 'number' && pricing.prompt === 0 && 
                      typeof pricing.completion === 'number' && pricing.completion === 0);
      
      return {
        id: model.id,
        name: model.name || model.id,
        isFree: isFree,
        isAvailable: true,
        lastChecked: Date.now(),
        source: 'api'
      };
    });

    // Sort: free models first, then paid models
    models.sort((a, b) => {
      if (a.isFree !== b.isFree) {
        return a.isFree ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });

    return models;
  } catch (error) {
    console.error('Error fetching models from API:', error);
    throw error;
  }
}

/**
 * Get cached models from localStorage
 * @returns {Object|null} Cached models with timestamp or null
 */
export function getCachedModels() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed = JSON.parse(cached);
    return parsed;
  } catch (error) {
    console.error('Error reading cached models:', error);
    return null;
  }
}

/**
 * Store models in localStorage cache
 * @param {Array} models - Array of model objects
 */
export function setCachedModels(models) {
  try {
    const cacheData = {
      models: models,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error caching models:', error);
  }
}

/**
 * Check if cache is still valid
 * @returns {boolean} True if cache exists and is not expired
 */
export function isCacheValid() {
  const cached = getCachedModels();
  if (!cached || !cached.timestamp) return false;
  
  const age = Date.now() - cached.timestamp;
  return age < CACHE_EXPIRY;
}

/**
 * Merge API models with hardcoded models, preserving hardcoded favorites
 * @param {Array} apiModels - Models from API
 * @param {Array} hardcodedModels - Hardcoded models
 * @returns {Array} Merged and deduplicated model list
 */
export function mergeModels(apiModels, hardcodedModels) {
  const modelMap = new Map();
  
  // First, add all API models
  apiModels.forEach(model => {
    modelMap.set(model.id, { ...model, source: 'api' });
  });
  
  // Then, add hardcoded models (they take precedence for availability status)
  hardcodedModels.forEach(model => {
    const existing = modelMap.get(model.id);
    if (existing) {
      // Merge: keep API data but preserve hardcoded availability if set
      modelMap.set(model.id, {
        ...existing,
        isAvailable: model.isAvailable !== undefined ? model.isAvailable : existing.isAvailable,
        source: 'merged'
      });
    } else {
      // Add hardcoded model that's not in API (might be unavailable)
      modelMap.set(model.id, {
        ...model,
        isAvailable: false, // Mark as potentially unavailable
        source: 'hardcoded'
      });
    }
  });
  
  return Array.from(modelMap.values());
}

/**
 * Refresh models from API and update cache
 * @param {string} apiKey - OpenRouter API key
 * @returns {Promise<Array>} Updated model list
 */
export async function refreshModelsFromAPI(apiKey = null) {
  try {
    const apiModels = await fetchModelsFromAPI(apiKey);
    const merged = mergeModels(apiModels, AI_MODELS);
    setCachedModels(merged);
    
    // Check for new models
    const cached = getCachedModels();
    if (cached && cached.models) {
      const newModels = apiModels.filter(apiModel => 
        !cached.models.some(cachedModel => cachedModel.id === apiModel.id)
      );
      if (newModels.length > 0) {
        // Store notification
        localStorage.setItem('openrouter_new_models', JSON.stringify({
          count: newModels.length,
          models: newModels,
          timestamp: Date.now()
        }));
      }
    }
    
    return merged;
  } catch (error) {
    console.error('Error refreshing models:', error);
    throw error;
  }
}

/**
 * Validate if a model is available by making a test request
 * @param {string} modelId - Model ID to validate
 * @param {string} apiKey - OpenRouter API key
 * @returns {Promise<boolean>} True if model is available
 */
export async function validateModel(modelId, apiKey) {
  if (!apiKey || !modelId) return false;
  
  try {
    const response = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': 'https://fiddyscript.com',
        'X-Title': 'Fiddyscript AI Content Assistant'
      },
      body: JSON.stringify({
        model: modelId,
        messages: [{ role: 'user', content: 'test' }],
        max_tokens: 1
      })
    });
    
    return response.ok;
  } catch (error) {
    console.error(`Error validating model ${modelId}:`, error);
    return false;
  }
}

/**
 * Get available models with automatic cache/API fallback
 * @param {boolean} forceRefresh - Force refresh from API
 * @param {string} apiKey - OpenRouter API key (optional)
 * @returns {Promise<Array>} List of available models
 */
export async function getAvailableModels(forceRefresh = false, apiKey = null) {
  // If force refresh, fetch from API
  if (forceRefresh) {
    try {
      return await refreshModelsFromAPI(apiKey);
    } catch (error) {
      console.warn('Failed to refresh models, using cache or hardcoded:', error);
    }
  }
  
  // Check cache first
  if (isCacheValid()) {
    const cached = getCachedModels();
    if (cached && cached.models) {
      return cached.models;
    }
  }
  
  // Try to fetch from API if we have a key
  if (apiKey) {
    try {
      return await refreshModelsFromAPI(apiKey);
    } catch (error) {
      console.warn('Failed to fetch from API, using hardcoded models:', error);
    }
  }
  
  // Fallback to hardcoded models
  return AI_MODELS;
}

/**
 * Validate API key by making a test request
 * @param {string} apiKey - API key to validate
 * @returns {Promise<boolean>} True if valid, false otherwise
 */
export async function validateApiKey(apiKey) {
  if (!apiKey || apiKey.trim() === '') {
    return false;
  }

  try {
    const response = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': 'https://fiddyscript.com',
        'X-Title': 'Fiddyscript AI Content Assistant'
      },
      body: JSON.stringify({
        model: 'openai/gpt-3.5-turbo',
        messages: [
          {
            role: 'user',
            content: 'test'
          }
        ],
        max_tokens: 5
      })
    });

    return response.ok;
  } catch (error) {
    console.error('API Key Validation Error:', error);
    return false;
  }
}

/**
 * Generate chat message using OpenRouter API with conversation history
 * @param {Array} messages - Array of message objects with role and content
 * @param {string} model - The model ID to use
 * @param {string} apiKey - OpenRouter API key
 * @returns {Promise<string>} The generated content
 */
export async function generateChatMessage(messages, model, apiKey, onChunk) {
  return generateContent(messages, model, apiKey, onChunk);
}

export default {
  generateContent,
  generateChatMessage,
  getAvailableModels,
  validateApiKey,
  fetchModelsFromAPI,
  refreshModelsFromAPI,
  getCachedModels,
  setCachedModels,
  isCacheValid,
  mergeModels,
  validateModel,
  AI_MODELS
};

